import { useCallback, useEffect, useRef } from "react";
import { env } from "@/shared/config/env";

/**
 * Where the Studio wants the host to land when it closes the editor.
 *
 * `studio:close` carries two different intents today — the editor's
 * breadcrumb means "back to the gallery", while the logo and the Dashboard
 * button mean "leave the editor entirely". One message can't serve both, so
 * the Studio names its destination.
 *
 * Absent → "templates", which is the behaviour every existing Studio build
 * gets without changing anything.
 */
export type StudioCloseTarget = "templates" | "dashboard";

export interface StudioLifecycleHandlers {
  onDirtyChange?: (dirty: boolean) => void;
  onSaved?: () => void;
  onPublished?: () => void;
  onClose?: (to: StudioCloseTarget) => void;
  onResize?: (height: number) => void;
  /**
   * The embedded gallery asks the host to open the full-screen editor route
   * for a template. The Studio cannot navigate the top-level page itself
   * (sandbox has no allow-top-navigation) — the host owns its routing.
   */
  onOpenEditor?: (templateVersionId: string) => void;
  /**
   * The embedded gallery asks for a template's FULL-SCREEN preview page.
   *
   * Same reason as `onOpenEditor`: the Studio cannot navigate the top-level
   * page. Distinct from it because the destinations differ — this one is
   * /templates/<id>/preview, which shows the template without adopting it or
   * opening an editing session.
   */
  onOpenPreview?: (templateVersionId: string) => void;
  /**
   * The merchant picked a template in the gallery running as a PICKER
   * (`/templates?mode=select`) — onboarding's theme step.
   *
   * A notification, not a request: in that mode the Studio adopts nothing and
   * navigates nowhere, because writing the choice is the wizard's job and
   * carries its own dirty-check, completion latch and diverged-state
   * recovery. The host is told what was picked and decides what it means.
   *
   * `name` is the name the STUDIO resolved, not the backend's raw
   * `templateName` — which is the reason onboarding embeds this gallery
   * rather than rendering a second one of its own (the Studio renames
   * templates whose backend name is a customer's brand).
   */
  onTemplateSelected?: (selection: {
    templateVersionId: string;
    templateCode: string;
    name: string;
  }) => void;
  /**
   * The Studio opened/closed a full-screen overlay (e.g. the template
   * preview modal). A modal inside a content-height iframe can only cover
   * the frame area — the host reacts by expanding the frame to cover the
   * whole window while the overlay is up, so it renders like a native
   * full-window modal.
   */
  onOverlay?: (open: boolean) => void;
  /**
   * The Studio asks the host to scroll an element into view. Inside the
   * content-height frame the inner document never scrolls, so
   * scrollIntoView is a no-op there — the guide/tour posts the element's
   * document position instead and the host scrolls its own window.
   * `top`/`height` are the element's offset and size within the Studio's
   * document.
   */
  onScrollTo?: (top: number, height: number) => void;
  /**
   * The Studio's page guide is running: its own mask dims the frame area,
   * but the dashboard chrome around the frame stays bright — so the host
   * dims everything OUTSIDE the frame with matching veil strips. Unlike
   * studio:overlay, the layout does not change: the frame stays in flow,
   * chrome stays visible (dimmed), and the page can still scroll.
   */
  onDim?: (on: boolean) => void;
  /**
   * The Studio has mounted and installed its own `studio:context` listener.
   *
   * Exists because the iframe's `load` event is not a safe cue on its own:
   * it says the DOCUMENT finished loading, not that React has mounted the
   * bridge that listens for our context. A context posted into that gap is
   * dropped silently, and the Studio boots to its embedded default (light)
   * while the dashboard sits in dark — the "black strip" class of bug.
   *
   * Optional and forward-compatible: Studio builds that never send
   * `studio:ready` simply keep the load-event behaviour.
   */
  onReady?: () => void;
  /**
   * Real content is on screen — the gallery's cards (or its empty/error
   * state), the editor's canvas.
   *
   * Distinct from `onReady` and `onResize`, both of which fire from the
   * Studio's MOUNT and were briefly overloaded as paint proxies. They say
   * "React is alive"; this says "the wait is over", which is the only one of
   * the three the host's loader should key on. The Studio posts it on every
   * terminal state, including the failures — a broken fetch must not sit
   * under our loader until the fallback timer expires.
   */
  onContentReady?: () => void;
  /**
   * The Studio has hit a dead end and cannot render this surface.
   *
   * Distinct from `onClose`, which means the merchant is finished. The host
   * must NOT navigate on this: with the legacy template surfaces removed
   * there is nothing behind the Studio, and routing a failed gallery back to
   * the gallery is a loop. Show an error state with a retry instead.
   *
   * The Studio posts `studio:content-ready` immediately after, so a host that
   * ignores this message still drops its loader and shows the Studio's own
   * error screen — the pre-existing behaviour.
   */
  onError?: (code: StudioErrorCode, message?: string) => void;
}

/**
 * Why the Studio cannot render. Validated against this set rather than passed
 * through: an unrecognised code from a newer Studio build must still produce
 * an error state, not `undefined` leaking into the UI.
 */
export type StudioErrorCode =
  | "SESSION_EXPIRED"
  | "RENDER_ERROR"
  | "LOAD_FAILED"
  | "NOT_FOUND";

const STUDIO_ERROR_CODES: readonly StudioErrorCode[] = [
  "SESSION_EXPIRED",
  "RENDER_ERROR",
  "LOAD_FAILED",
  "NOT_FOUND",
];

export interface StudioContext {
  storeId?: string;
  templateId?: string;
  theme: "light" | "dark";
  locale: string;
  /**
   * Picker mode: the template version the host currently has selected, so
   * the gallery can mark that card. Sent over context rather than the URL
   * because it changes after boot (the host pre-selects once its own list
   * loads) and a URL change would reload the frame.
   */
  selectedTemplateVersionId?: string;
}

/**
 * The session channel that sits alongside the backend's auth contract.
 *
 * The auth guides cover token exchange only. An embedded editor also has to
 * tell its host about unsaved changes (so back-navigation can be guarded),
 * about save/publish (for host-level feedback), about closing, and — for the
 * in-shell gallery — about its content height.
 *
 * These carry no requestId and use a distinct `studio:` type prefix, so they
 * cannot collide with TEMPLATE_EDITOR_AUTH_* traffic.
 */
export function useStudioLifecycle(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  handlers: StudioLifecycleHandlers,
): { postContext: (context: StudioContext) => void } {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Host → studio. The auth reply carries only the token, so theme and locale
  // arrive here or the editor paints in the wrong theme first.
  //
  // Memoised on purpose: callers drive it from an effect that re-posts when the
  // dashboard theme or locale changes, and an identity that changed every
  // render would make that effect fire on every render instead.
  const postContext = useCallback(
    (context: StudioContext) => {
      const studioOrigin = env.STUDIO_ORIGIN;
      const win = iframeRef.current?.contentWindow;
      if (!studioOrigin || !win) return;
      win.postMessage({ type: "studio:context", ...context }, studioOrigin);
    },
    [iframeRef],
  );

  useEffect(() => {
    const studioOrigin = env.STUDIO_ORIGIN;
    if (!studioOrigin) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== studioOrigin) return;
      const data = event.data as { type?: unknown } | null;
      if (!data || typeof data !== "object") return;

      const h = handlersRef.current;
      switch (data.type) {
        case "studio:dirty": {
          const { dirty } = data as { dirty?: unknown };
          if (typeof dirty === "boolean") h.onDirtyChange?.(dirty);
          break;
        }
        case "studio:saved":
          h.onSaved?.();
          break;
        case "studio:published":
          h.onPublished?.();
          break;
        case "studio:close": {
          // Validated against the known set rather than passed through: an
          // unrecognised value falls back to the historical destination
          // instead of routing the vendor somewhere arbitrary.
          const { to } = data as { to?: unknown };
          h.onClose?.(to === "dashboard" ? "dashboard" : "templates");
          break;
        }
        case "studio:resize": {
          const { height } = data as { height?: unknown };
          if (typeof height === "number") h.onResize?.(height);
          break;
        }
        case "studio:open-editor": {
          const { templateVersionId } = data as { templateVersionId?: unknown };
          if (typeof templateVersionId === "string" && templateVersionId) {
            h.onOpenEditor?.(templateVersionId);
          }
          break;
        }
        case "studio:open-preview": {
          const { templateVersionId } = data as { templateVersionId?: unknown };
          if (typeof templateVersionId === "string" && templateVersionId) {
            h.onOpenPreview?.(templateVersionId);
          }
          break;
        }
        case "studio:template-selected": {
          // All three fields are required rather than optional: the host
          // writes them straight into its step payload, and a half-filled
          // selection would save a template with no name against it.
          const { templateVersionId, templateCode, name } = data as {
            templateVersionId?: unknown;
            templateCode?: unknown;
            name?: unknown;
          };
          if (
            typeof templateVersionId === "string" &&
            templateVersionId &&
            typeof templateCode === "string" &&
            typeof name === "string"
          ) {
            h.onTemplateSelected?.({ templateVersionId, templateCode, name });
          }
          break;
        }
        case "studio:overlay": {
          const { open } = data as { open?: unknown };
          if (typeof open === "boolean") h.onOverlay?.(open);
          break;
        }
        case "studio:scroll-to": {
          const { top, height } = data as { top?: unknown; height?: unknown };
          if (typeof top === "number" && Number.isFinite(top)) {
            h.onScrollTo?.(top, typeof height === "number" ? height : 0);
          }
          break;
        }
        case "studio:dim": {
          const { on } = data as { on?: unknown };
          if (typeof on === "boolean") h.onDim?.(on);
          break;
        }
        case "studio:ready":
          h.onReady?.();
          break;
        case "studio:content-ready":
          h.onContentReady?.();
          break;
        case "studio:error": {
          const { code, message } = data as {
            code?: unknown;
            message?: unknown;
          };
          // An unknown code still means "failed" — fall back to RENDER_ERROR
          // rather than dropping the message, or a newer Studio's new code
          // would silently leave the merchant on a blank frame.
          const known = STUDIO_ERROR_CODES.includes(code as StudioErrorCode)
            ? (code as StudioErrorCode)
            : "RENDER_ERROR";
          h.onError?.(
            known,
            typeof message === "string" ? message : undefined,
          );
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef]);

  return { postContext };
}
